import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type LoginRequest,
  type LoginResponse,
  type LogoutAllRequest,
  type LogoutAllResponse,
  type UpdateMeRequest,
  type UpdateMeResponse,
  type UserResponse,
  publicApiClient,
} from "@/lib/api/client";
import { getApiErrorMessage } from "@/lib/api/errors";

import { LoginFailedError } from "./errors";
import { authKeys } from "./keys";
import { meQueryOptions } from "./queries";

/** Current user from session cookie / bearer token. `null` when unauthenticated. */
export function useCurrentUser() {
  return useQuery(meQueryOptions());
}

/** Log in with email and password; primes the profile cache from the response. */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginRequest): Promise<LoginResponse> => {
      const { data, error, response } = await publicApiClient.POST("/api/v1/auth/login", {
        body: credentials,
      });

      if (response.status === 401) {
        throw new LoginFailedError();
      }

      if (error || !data) {
        throw new Error(getApiErrorMessage(error, "Login failed."));
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UserResponse | null>(authKeys.me, data.user);
    },
  });
}

/** Revoke the current session and clear cached profile data. */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const { error, response } = await publicApiClient.POST("/api/v1/auth/logout");

      if (!response.ok) {
        throw new Error(getApiErrorMessage(error, "Logout failed."));
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(authKeys.me, null);
      queryClient.removeQueries({ queryKey: authKeys.me });
    },
  });
}

/** Change password and/or display name; primes the profile cache from the response. */
export function useUpdateMe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateMeRequest): Promise<UpdateMeResponse> => {
      const { data, error } = await publicApiClient.PATCH("/api/v1/me", { body });

      if (error || !data) {
        throw new Error(getApiErrorMessage(error, "Profile update failed."));
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UserResponse | null>(authKeys.me, data);
    },
  });
}

/** Revoke every session for the current user (password confirmation) and clear the cache. */
export function useLogoutAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: LogoutAllRequest): Promise<LogoutAllResponse> => {
      const { data, error } = await publicApiClient.POST("/api/v1/auth/logout-all", { body });

      if (error || !data) {
        throw new Error(getApiErrorMessage(error, "Failed to sign out of all sessions."));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.setQueryData(authKeys.me, null);
      queryClient.removeQueries({ queryKey: authKeys.me });
    },
  });
}
